import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { ReleasesTable } from '@/features/releases/components/ReleasesTable';
import { queryReleases } from '@/features/releases/data';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { Box, Panel } from '@/ui';
import { buildContainsFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { Stack } from '@/ui/layout/Stack';
import {
  releasesTableDefinition,
  releasesSortFieldMap,
  type ReleasesSortDirection,
  type ReleasesSortKey,
} from './releasesTableDefinition';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { QueryResult, ReleaseListItemDto } from '@flowqa/shared';

type ReleasesTablePanelProps = {
  fixedFilters: GridQueryInput['filters'];
  rangeStartDay: string | null;
  rangeEndDay: string | null;
  noRowsText: string;
  searchValue?: string;
};

const EMPTY_SEARCH = '';
const EMPTY_TAGS: string[] = [];
const EMPTY_STATUSES: Array<ReleaseListItemDto['status']> = [];

export const ReleasesTablePanel = ({
  fixedFilters,
  rangeStartDay,
  rangeEndDay,
  noRowsText,
  searchValue = '',
}: ReleasesTablePanelProps): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    releasesTableDefinition.defaultColumnVisibilityModel,
  );
  const [sortKey, setSortKey] = useState<ReleasesSortKey>(releasesTableDefinition.defaultSortKey);
  const [sortDirection, setSortDirection] = useState<ReleasesSortDirection>(
    releasesTableDefinition.defaultSortDirection,
  );
  const fixedFiltersKey = useMemo(() => JSON.stringify(fixedFilters), [fixedFilters]);
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<ReleaseListItemDto>> =>
      queryReleases(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        filters: mergeFilters(input.filters, buildContainsFilter('name', searchValue)),
        sorts: [{ field: releasesSortFieldMap[sortKey], direction: sortDirection }],
      }),
    [searchValue, selectedOrganisationId, selectedProjectId, sortDirection, sortKey],
  );

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }} />
            <Box sx={{ minWidth: 0, flex: '0 1 auto' }}>
              <TableToolbar<ReleasesSortKey, ReleaseListItemDto['status']>
                title={undefined}
                searchAriaLabel={releasesTableDefinition.searchAriaLabel}
                searchValue={EMPTY_SEARCH}
                onDebouncedSearchChange={() => undefined}
                columnVisibilityModel={columnVisibilityModel}
                defaultColumnVisibilityModel={releasesTableDefinition.defaultColumnVisibilityModel}
                columnOptions={releasesTableDefinition.columnOptions}
                onColumnVisibilityModelChange={(nextModel) => {
                  setColumnVisibilityModel({
                    ...nextModel,
                    name: true,
                  });
                }}
                sortKey={sortKey}
                sortDirection={sortDirection}
                defaultSortKey={releasesTableDefinition.defaultSortKey}
                defaultSortDirection={releasesTableDefinition.defaultSortDirection}
                sortOptions={releasesTableDefinition.sortOptions}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setSortKey(nextSortKey);
                  setSortDirection(nextSortDirection);
                }}
                filterStatuses={EMPTY_STATUSES}
                statusOptions={releasesTableDefinition.statusOptions}
                onFilterStatusesChange={() => undefined}
                filterTags={EMPTY_TAGS}
                onFilterTagsChange={() => undefined}
                dateQuickFilter="latest"
                onDateQuickFilterChange={() => undefined}
                fromDate={null}
                toDate={null}
                latestQueryStartDay={rangeStartDay}
                latestQueryEndDay={rangeEndDay}
                onFromDateChange={() => undefined}
                onToDateChange={() => undefined}
                availableTags={EMPTY_TAGS}
              />
            </Box>
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      <ReleasesTable
        persistKey={null}
        fixedFilters={fixedFilters}
        initialPageSize={5}
        queryFn={queryFn}
        queryKey={[
          'releases-table',
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          sortKey,
          sortDirection,
          rangeStartDay,
          rangeEndDay,
          fixedFiltersKey,
        ]}
        noRowsText={noRowsText}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(nextModel) => {
          setColumnVisibilityModel({
            ...nextModel,
            name: true,
          });
        }}
      />
    </Panel>
  );
};
